import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import '@/App.css';

// Context
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';

// Layout Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import AIChat from './components/AIChat';

// Pages
import HomePage from './pages/HomePage';
import ServicesPage from './pages/ServicesPage';
import WhoWeHelpPage from './pages/WhoWeHelpPage';
import HowItWorksPage from './pages/HowItWorksPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import FAQPage from './pages/FAQPage';
import BlogPage from './pages/BlogPage';
import BookAppointmentPage from './pages/BookAppointmentPage';
import GetQuotePage from './pages/GetQuotePage';
import LegalPage from './pages/LegalPage';
import AuthPage from './pages/AuthPage';
import AuthCallback from './pages/AuthCallback';
import ClientPortal from './pages/ClientPortal';
import AdminDashboard from './pages/AdminDashboard';
import { PaymentSuccessPage, PaymentCancelPage } from './pages/PaymentPages';
import RequestChangesPage from './pages/RequestChangesPage';
import TwoFactorSetupPage from './pages/TwoFactorSetupPage';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#0B1F3A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#475569]">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/portal" replace />;
  }

  return children;
};

// Layout wrapper - determines where to show AI Chat based on route
const AppLayout = () => {
  const location = useLocation();
  const showAIChat = ['/faq', '/contact', '/portal'].some(path => 
    location.pathname === path || location.pathname.startsWith('/portal')
  );
  
  const isPortalOrAdmin = location.pathname.startsWith('/portal') || location.pathname.startsWith('/admin');

  return (
    <>
      <Navbar />
      <main className={isPortalOrAdmin ? '' : 'min-h-screen'}>
        <Routes>
          {/* Public Pages */}
          <Route path="/" element={<HomePage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/who-we-help" element={<WhoWeHelpPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPage />} />
          <Route path="/book-appointment" element={<BookAppointmentPage />} />
          <Route path="/get-quote" element={<GetQuotePage />} />
          
          {/* Auth Pages */}
          <Route path="/login" element={<AuthPage mode="login" />} />
          <Route path="/register" element={<AuthPage mode="register" />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          
          {/* Legal Pages */}
          <Route path="/privacy" element={<LegalPage page="privacy" />} />
          <Route path="/terms" element={<LegalPage page="terms" />} />
          <Route path="/circular-230" element={<LegalPage page="circular230" />} />
          
          {/* Payment Pages (Public) */}
          <Route path="/payment/success" element={<PaymentSuccessPage />} />
          <Route path="/payment/cancel" element={<PaymentCancelPage />} />
          <Route path="/request-changes" element={<RequestChangesPage />} />
          
          {/* 2FA Setup (Protected) */}
          <Route path="/2fa-setup" element={
            <ProtectedRoute>
              <TwoFactorSetupPage />
            </ProtectedRoute>
          } />
          
          {/* Protected Routes */}
          <Route path="/portal" element={
            <ProtectedRoute>
              <ClientPortal />
            </ProtectedRoute>
          } />
          
          <Route path="/admin" element={
            <ProtectedRoute adminOnly>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          
          {/* 404 */}
          <Route path="*" element={
            <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]" style={{ paddingTop: '72px' }}>
              <div className="text-center">
                <h1 className="font-heading text-6xl font-bold text-[#0B1F3A] mb-4">404</h1>
                <p className="text-[#475569] mb-6">Page not found</p>
                <a href="/" className="btn-primary px-6 py-3">Go Home</a>
              </div>
            </div>
          } />
        </Routes>
      </main>
      {!isPortalOrAdmin && <Footer />}
      {showAIChat && <AIChat />}
      <Toaster position="top-right" richColors />
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LanguageProvider>
          <AppLayout />
        </LanguageProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
